/** Dados dos briefings e envio de arquivos ao Google Drive do escritório. */
import { useQuery } from '@tanstack/react-query';
import type { Briefing, BriefingFile, BriefingVersion, DesignerStats } from '@dotapps/core';
import { api } from '../../lib/api';

export interface DriveStatus {
  enabled: boolean;
  connected: boolean;
  email: string | null;
  folderUrl: string | null;
}

export const bkeys = {
  all: ['briefings'],
  one: (id: string) => ['briefings', id],
  stats: ['briefings', 'stats'],
  drive: ['drive'],
} as const;

export const useBriefings = () =>
  useQuery({ queryKey: bkeys.all, queryFn: () => api.get<Briefing[]>('/briefings'), staleTime: 20_000 });
export const useBriefing = (id: string) =>
  useQuery({ queryKey: bkeys.one(id), queryFn: () => api.get<Briefing>(`/briefings/${id}`) });
export const useBriefingStats = (enabled: boolean) =>
  useQuery({
    queryKey: bkeys.stats,
    queryFn: () => api.get<DesignerStats[]>('/briefings/stats'),
    enabled,
    staleTime: 60_000,
  });
export const useDrive = () =>
  useQuery({ queryKey: bkeys.drive, queryFn: () => api.get<DriveStatus>('/drive'), staleTime: 60_000 });

export interface BriefingInput {
  clientId: string;
  dealId: string | null;
  companyId: string | null;
  titulo: string;
  detalhes: string;
  medidas: string;
  prazo: string | null;
  designerId: string | null;
}

export const briefingCalls = {
  create: (b: BriefingInput) => api.post<Briefing>('/briefings', b),
  update: (id: string, b: Partial<BriefingInput>) => api.patch<Briefing>(`/briefings/${id}`, b),
  remove: (id: string) => api.del(`/briefings/${id}`),
  action: (id: string, body: { acao: 'iniciar' | 'retomar' } | { acao: 'pausar'; motivo: string; nota: string }) =>
    api.post<Briefing>(`/briefings/${id}/action`, body),
  version: (id: string, nota: string) => api.post<BriefingVersion>(`/briefings/${id}/versions`, { nota }),
  review: (id: string, vid: string, aprovar: boolean, review: string) =>
    api.post<Briefing>(`/briefings/${id}/versions/${vid}/review`, { aprovar, review }),
  toBudget: (id: string) => api.post<Briefing>(`/briefings/${id}/budget`),
  link: (id: string, nome: string, url: string, versionId: string | null) =>
    api.post<BriefingFile>(`/briefings/${id}/links`, { nome, url, versionId }),
  removeFile: (id: string, fid: string) => api.del(`/briefings/${id}/files/${fid}`),
};

/**
 * Envia um arquivo ao Drive: a API abre a sessão, o navegador manda o arquivo direto
 * ao Google (com progresso) e a API registra o link.
 */
export async function uploadToDrive(
  briefingId: string,
  file: File,
  versionId: string | null,
  onProgress: (pct: number) => void,
): Promise<BriefingFile> {
  const { uploadUrl } = await api.post<{ uploadUrl: string }>(`/briefings/${briefingId}/uploads`, {
    nome: file.name,
    mime: file.type || 'application/octet-stream',
    size: file.size,
    versionId,
  });
  const driveId = await new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.upload.onprogress = e => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      try {
        if (xhr.status >= 200 && xhr.status < 300) return resolve(JSON.parse(xhr.responseText).id as string);
      } catch {
        /* resposta inesperada */
      }
      reject(new Error(`Falha ao enviar ${file.name}.`));
    };
    xhr.onerror = () => reject(new Error(`Sem conexão ao enviar ${file.name}.`));
    xhr.send(file);
  });
  return api.post<BriefingFile>(`/briefings/${briefingId}/files`, { driveFileId: driveId, versionId });
}
